import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { memo, useMemo, useState } from "react";
import {
  setHotelFilters,
  useHotelFilter,
} from "../../state/extract-hotel-filter-slice";
import { HotelAccordion } from "./hotel-accordion";

export const HotelFacilitiesFilter = memo(() => {
  const dispatch = useAppDispatch();
  const { defaultOption, selectedOption } = useAppSelector(useHotelFilter);
  const isLoading = useAppSelector(
    (state) => state.selectedHotelData.isLoading,
  );

  const state = defaultOption?.facilities as string[];
  const selected = selectedOption?.facilities;

  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");

  const handleChange = (value?: string) => {
    if (!value) return;

    const isSelected = selected?.some((item) => item === value);

    const updatedValue = isSelected
      ? selected?.filter((item) => item !== value)
      : [...(selected ?? []), value];

    dispatch(setHotelFilters({ facilities: updatedValue }));
  };

  // Filter facilities based on search
  const filteredList = useMemo(() => {
    if (!state) return [];
    return state.filter((item) =>
      item.toLowerCase().includes(search.toLowerCase()),
    );
  }, [state, search]);

  // Show only first 10 unless expanded
  const visibleList = showAll ? filteredList : filteredList.slice(0, 10);

  return (
    <HotelAccordion value={"facilities"} label={"Facilities"} className="">
      <div className="space-y-2">
        {/* Search bar */}
        <Input
          disabled={isLoading}
          placeholder="Search facilities..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm rounded-md border focus-visible:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary placeholder:text-gray-400"
        />

        {/* Facility checkboxes */}
        <div className="space-y-2">
          {visibleList.map((item, index) => (
            <div key={index} className="flex items-center gap-2 py-1">
              <Checkbox
                disabled={isLoading}
                id={item}
                onCheckedChange={() => handleChange(item)}
                checked={selected?.includes(item) ?? false}
              />
              <Label
                htmlFor={item}
                title={item}
                className="font-normal cursor-pointer w-full"
              >
                <span className=" block mr-1">{item}</span>
              </Label>
            </div>
          ))}
        </div>

        {/* Show more/less toggle */}
        {filteredList.length > 10 && (
          <Button
            variant="link"
            className="px-0"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll
              ? "▲ Show Less"
              : `▼ Show ${filteredList.length - 10} More`}
          </Button>
        )}
      </div>
    </HotelAccordion>
  );
});

HotelFacilitiesFilter.displayName = "HotelFacilitiesFilter";
